import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import { schemaEcritureFicheFacturation } from '@artosera/shared';
import { useShellContext } from '../shell/context.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import {
  useFicheFacturationArtosera,
  useEcrireFicheFacturationArtosera,
  type FicheFacturationArtosera,
} from '../../api/facturationArtosera.js';
import { useProfilFiscal } from '../../api/ventes.js';
import { modeDemonstrationActif } from '../../api/demo.js';
import styles from './ReglagesPage.module.css';
import form from '../presentation/Presentation.module.css';

export function FacturationArtoseraPage() {
  const { t } = useTranslation('regie');
  const { galerieSlug, galerie, compte } = useShellContext();
  const autorise = galerieALaction(galerie, 'addons:billing');
  const requete = useFicheFacturationArtosera(galerieSlug, autorise);
  if (!autorise) return <p role="alert">{t('reglages.facturationArtosera.interdit')}</p>;
  return (
    <div className={styles.page}>
      <h1 className={styles.titre}>{t('reglages.facturationArtosera.titre')}</h1>
      {requete.isPending ? <p role="status">{t('reglages.marketplace.chargement')}</p> : null}
      {requete.isError ? <p role="alert">{t('reglages.facturationArtosera.erreur')}</p> : null}
      {requete.data ? (
        <Formulaire
          key={`${compte.id}:${galerieSlug}:${requete.data.revisionFicheFacturation}`}
          donnees={requete.data}
        />
      ) : null}
    </div>
  );
}

function Formulaire({ donnees }: { donnees: FicheFacturationArtosera }) {
  const { t, i18n } = useTranslation('regie');
  const { galerieSlug, galerie } = useShellContext();
  const fiche = donnees.ficheFacturation;
  const [valeurs, setValeurs] = useState({
    raisonSociale: fiche?.raisonSociale ?? '',
    ligne1: fiche?.adresse.ligne1 ?? '',
    ligne2: fiche?.adresse.ligne2 ?? '',
    codePostal: fiche?.adresse.codePostal ?? '',
    ville: fiche?.adresse.ville ?? '',
    pays: fiche?.adresse.pays ?? '',
    siren: fiche?.siren ?? '',
    tvaIntracom: fiche?.tvaIntracom ?? '',
    emailFacturation: fiche?.emailFacturation ?? '',
  });
  const [invalide, setInvalide] = useState(false);
  const profil = useProfilFiscal(galerieSlug, { enabled: false });
  const mutation = useEcrireFicheFacturationArtosera(galerieSlug);
  const demo = modeDemonstrationActif();
  return (
    <>
      <p>
        {t(
          fiche?.tvaVerifieeLe && valeurs.tvaIntracom === fiche.tvaIntracom
            ? 'reglages.facturationArtosera.verifiee'
            : 'reglages.facturationArtosera.nonVerifiee',
        )}
        {fiche?.tvaVerifieeLe && valeurs.tvaIntracom === fiche.tvaIntracom
          ? ` · ${new Date(fiche.tvaVerifieeLe).toLocaleString(i18n.language)}`
          : ''}
      </p>
      {galerieAModule(galerie, 'facturation') ? (
        <Button
          disabled={demo || profil.isFetching}
          onClick={async () => {
            const resultat = await profil.refetch();
            const emetteur = resultat.data?.profilFiscal.emetteur;
            if (emetteur)
              setValeurs({
                raisonSociale: emetteur.raisonSociale,
                ligne1: emetteur.adresse.ligne1,
                ligne2: emetteur.adresse.ligne2 ?? '',
                codePostal: emetteur.adresse.codePostal,
                ville: emetteur.adresse.ville,
                pays: emetteur.adresse.pays,
                siren: emetteur.siren ?? '',
                tvaIntracom: emetteur.tvaIntracom ?? '',
                emailFacturation: emetteur.courriel ?? '',
              });
          }}
        >
          {t('reglages.facturationArtosera.reprendre')}
        </Button>
      ) : null}
      {profil.isError ? <p role="alert">{t('reglages.facturationArtosera.erreurProfil')}</p> : null}
      <form
        className={form.formulaire}
        onSubmit={(event) => {
          event.preventDefault();
          if (demo || mutation.isPending) return;
          const resultat = schemaEcritureFicheFacturation.safeParse({
            raisonSociale: valeurs.raisonSociale,
            adresse: {
              ligne1: valeurs.ligne1,
              ...(valeurs.ligne2 ? { ligne2: valeurs.ligne2 } : {}),
              codePostal: valeurs.codePostal,
              ville: valeurs.ville,
              pays: valeurs.pays,
            },
            ...(valeurs.siren ? { siren: valeurs.siren } : {}),
            ...(valeurs.tvaIntracom ? { tvaIntracom: valeurs.tvaIntracom } : {}),
            emailFacturation: valeurs.emailFacturation,
            revisionFicheFacturation: donnees.revisionFicheFacturation,
          });
          setInvalide(!resultat.success);
          if (resultat.success) mutation.mutate(resultat.data);
        }}
      >
        {(Object.keys(valeurs) as (keyof typeof valeurs)[]).map((champ) => (
          <label key={champ}>
            {t(`reglages.facturationArtosera.${champ}`)}
            <Input
              value={valeurs[champ]}
              onChange={(e) => {
                setValeurs({ ...valeurs, [champ]: e.target.value });
              }}
              type={champ === 'emailFacturation' ? 'email' : 'text'}
              maxLength={champ === 'pays' ? 2 : 254}
              required={!['ligne2', 'siren', 'tvaIntracom'].includes(champ)}
              readOnly={demo}
            />
          </label>
        ))}
        {invalide ? <p role="alert">{t('reglages.facturationArtosera.invalide')}</p> : null}
        {mutation.isError ? <p role="alert">{t('reglages.facturationArtosera.erreur')}</p> : null}
        {demo ? <p>{t('reglages.facturationArtosera.demo')}</p> : null}
        <Button type="submit" disabled={demo || mutation.isPending}>
          {t('reglages.facturationArtosera.enregistrer')}
        </Button>
      </form>
    </>
  );
}
